/** @format */

import React, { FC, useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  _$,
  Select,
  Option,
  Table,
  TableColumn,
  Pagination,
  Tag,
  Message,
  Ellipsis
} from '@king-design/react'
import { MonitorSearch, TableSearch, parseTableSearchParams } from 'src/components'
import { PrometheusAlarmTabProps } from 'src/pages/PrometheusMonitor/Alarm'
import { PageProps, initPage } from 'src/utils/interface'
import { useHistory, useLocation } from 'react-router-dom'
import { useAsyncFn } from 'react-use'
import { getPrometheusAlerting, getPrometheusAlertPolicyList } from 'src/api/ApiPrometheusAlarm'

interface SearchProps extends PageProps {
  Instance: string
  PolicyName: string
}

const initSearch: SearchProps = {
  Instance: '',
  PolicyName: '',
  ...initPage
}

const PrometheusAlarming: FC<PrometheusAlarmTabProps> = ({ className, region, instances }) => {
  const history = useHistory()
  const location = useLocation()
  const [search, setSearch] = useState<SearchProps>(
    () => parseTableSearchParams<SearchProps>(location.search) || initSearch
  )

  const [{ value: tableData }, fetch] = useAsyncFn(
    async (params?) => {
      const result = await getPrometheusAlerting({
        ...search,
        ...params,
        Region: region
      })
      return {
        data: result?.Records || [],
        total: result?.TotalCount || 0
      }
    },
    [search, region]
  )

  useEffect(() => {
    fetch()
  }, [search, region])

  const toPolicyPage = async (policyID, ruleName?) => {
    if (policyID) {
      const result = await getPrometheusAlertPolicyList({
        PolicyID: parseInt(policyID)
      })
      if (result?.Polices?.length) {
        let url = `/monitor/prometheusmonitor/alarm/edit?tab=alarming&page=policy&policyid=${policyID}`
        if (ruleName) {
          url += `&rulename=${ruleName}`
        }
        history.push(url)
      } else {
        Message.error(_$('策略不存在'))
      }
    } else {
      Message.error(_$('策略ID为空'))
    }
  }

  return (
    <div className={className}>
      <TableSearch params={search}>
        <div className="search_item">
          <Select
            value={search.Instance}
            clearable
            filterable
            placeholder={_$('请选择关联资源')}
            slotPrefix={`${_$('关联资源')}：`}
            onChangeValue={value => {
              setSearch({
                ...search,
                Page: 1,
                Instance: value
              })
            }}
          >
            {instances?.map(item => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </div>
        <div className="search_item">
          <MonitorSearch
            placeholder={_$('请输入策略名称，支持模糊搜索')}
            value={search.PolicyName}
            prefix={`${_$('策略名称')}：`}
            onChange={value => {
              setSearch({
                ...search,
                Page: 1,
                PolicyName: value
              })
            }}
          />
        </div>
      </TableSearch>
      <Table
        stickHeader={50}
        data={tableData?.data || []}
        animation={false}
        rowKey={data => data.ID}
        checkType="none"
      >
        <TableColumn
          key="InstanceID"
          title={_$('关联资源')}
          slotTemplate={([data]) => {
            return data.InstanceID ? (
              <>
                <p>{data.InstanceName}</p>
                <Ellipsis className="table_text" maxLines={2} hoverable>
                  {data.InstanceID}
                </Ellipsis>
              </>
            ) : null
          }}
        ></TableColumn>
        <TableColumn
          key="RuleName"
          title={_$('策略 / 规则')}
          slotTemplate={([data]) => {
            return (
              <a
                onClick={() => {
                  toPolicyPage(data.PolicyID, data.RuleName)
                }}
              >{`${data.PolicyName} / ${data.RuleName}`}</a>
            )
          }}
        />
        <TableColumn key="CreatedAt" title={_$('触发时间')} width="100"></TableColumn>
        <TableColumn
          key="AlertStatus"
          title={_$('告警状态')}
          width="100"
          slotTemplate={([data]) => {
            switch (data.AlertStatus) {
              case 'firing':
                return <Tag type="danger">{_$('告警中')}</Tag>
              case 'silenced':
                return <Tag type="warning">{_$('静默')}</Tag>
              case 'inhibited':
                return <Tag type="warning">{_$('抑制')}</Tag>
              case 'silenced and inhibited':
                return <Tag type="warning">{_$('静默且抑制')}</Tag>
              default:
                // 没有
                return <Tag type="default">{_$('已恢复')}</Tag>
            }
          }}
        ></TableColumn>
        <TableColumn
          key="Labels"
          title={_$('标签')}
          slotTemplate={([data]) => {
            if (!data.Labels || !Array.isArray(data.Labels)) return '-'
            return (
              <div className="label-tags">
                {data.Labels.map((label, index) => {
                  if (label.Name === 'severity') {
                    return (
                      <p key={label.Name}>
                        {_$('告警等级（severity）')}= {label.Value}
                      </p>
                    )
                  } else {
                    return (
                      <p key={label.Name}>
                        {label.Name} = {label.Value}
                      </p>
                    )
                  }
                })}
              </div>
            )
          }}
        ></TableColumn>
        <TableColumn
          key="Summary"
          title={_$('告警摘要')}
          slotTemplate={([data]) => {
            return (
              <Ellipsis maxLines={2} hoverable>
                {data.Summary}
              </Ellipsis>
            )
          }}
        ></TableColumn>
        <TableColumn
          key="Description"
          title={_$('告警内容')}
          slotTemplate={([data]) => {
            return (
              <Ellipsis maxLines={2} hoverable>
                {data.Description}
              </Ellipsis>
            )
          }}
        ></TableColumn>
      </Table>
      {tableData !== undefined ? (
        <Pagination
          value={search.Page}
          limit={search.PageSize}
          total={tableData?.total || 0}
          size="small"
          onChangeValue={value => {
            setSearch({
              ...search,
              Page: value
            })
          }}
          onChangeLimit={limit => {
            setSearch({
              ...search,
              PageSize: limit
            })
          }}
        />
      ) : null}
    </div>
  )
}

export default styled(PrometheusAlarming)``
